import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { BarChart3, PieChart as PieChartIconLucide } from "lucide-react";
import "./RequisitionsDashboard.scss";
import { selectAuthState } from "../../../../store/selectors/authSelectors";
import { getWorkflowResultsp2p } from "../../../../ApiCalls/mantisFlowsManangment";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";

// Componentes de gráficas existentes
import GlobalStatusChart from "./components/GlobalStatusChart";
import AmountByStatusChart from "./components/AmountByStatusChart";
import MonthlyEvolutionChart from "./components/MonthlyEvolutionChart";
import PaymentProjectionChart from "./components/PaymentProjectionChart";
import ExecutionVsBudgetChart from "./components/ExecutionVsBudgetChart";
import UserRankingChart from "./components/UserRankingChart";
import CostCenterRankingChart from "./components/CostCenterRankingChart";
import RequisitionsByCompanyChart from "./components/RequisitionsByCompanyChart";
import AmountDistributionChart from "./components/AmountDistributionChart";
import ApprovalTimeChart from "./components/ApprovalTimeChart";
import WeeklyVolumeChart from "./components/WeeklyVolumeChart";
import RejectionRateChart from "./components/RejectionRateChart";
import CategoryExpenseChart from "./components/CategoryExpenseChart";
import UserUsageChart from "./components/UserUsageChart";
import HierarchicalMapChart from "./components/HierarchicalMapChart";
import AreaDistributionChart from "./components/AreaDistributionChart";

const sampleMonths = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

const RequisitionsDashboard = ({ mantisBusiness, token }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [selectedTimeRange, setSelectedTimeRange] = useState("30d");
  const [selectedFilters, setSelectedFilters] = useState({
    company: null,
    division: null,
    costCenter: null,
    status: null,
  });

  // Nuevo estado para datos de IPR
  const [iprData, setIprData] = useState([]);
  const [iprLoading, setIprLoading] = useState(false);
  const [iprError, setIprError] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadIPRData();
  }, [selectedTimeRange, selectedFilters, mantisBusiness?._id]);

  // Procesar datos de IPR para el dashboard de requisiciones
  const processIPRForDashboard = useMemo(() => {
    if (!iprData || iprData.length === 0) return null;

    // Mapear estados de IPR a estados del dashboard
    const statusMapping = {
      approved: "approved",
      complete: "approved",
      pending: "inProcess",
      error: "rejected",
      rejected: "rejected",
      cancelled: "rejected",
    };

    // Contar por estado mapeado
    const statusCounts = iprData.reduce((acc, ipr) => {
      const mappedStatus = statusMapping[ipr.status] || "inProcess";
      acc[mappedStatus] = (acc[mappedStatus] || 0) + 1;
      return acc;
    }, {});

    // Calcular montos por estado
    const amountByStatus = iprData.reduce((acc, ipr) => {
      const mappedStatus = statusMapping[ipr.status] || "inProcess";
      let amount = 0;
      if (ipr.total_amount && typeof ipr.total_amount === "string") {
        amount = parseFloat(ipr.total_amount.replace(/[^\d.-]/g, "")) || 0;
      }
      acc[mappedStatus] = (acc[mappedStatus] || 0) + amount;
      return acc;
    }, {});

    // Procesar evolución mensual (últimos 4 meses)
    const monthlyEvolution = [];
    const now = new Date();
    for (let i = 3; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString("es-ES", { month: "short" });

      const monthData = {
        month: monthKey,
        approved: 0,
        rejected: 0,
      };

      iprData.forEach((ipr) => {
        if (ipr.extraction_date) {
          try {
            const iprDate = new Date(ipr.extraction_date);
            if (
              iprDate.getMonth() === date.getMonth() &&
              iprDate.getFullYear() === date.getFullYear()
            ) {
              const mappedStatus = statusMapping[ipr.status] || "inProcess";
              if (mappedStatus === "approved") {
                monthData.approved++;
              } else if (mappedStatus === "rejected") {
                monthData.rejected++;
              }
            }
          } catch (error) {
            // Ignorar fechas inválidas
          }
        }
      });

      monthlyEvolution.push(monthData);
    }

    // Procesar ranking de usuarios por cantidad de IPRs
    const userRanking = {};
    iprData.forEach((ipr) => {
      // Check if we have the required field
      if (ipr.created_by_name) {
        const userName = ipr.created_by_name.toLowerCase().trim();

        if (!userRanking[userName]) {
          userRanking[userName] = {
            name: ipr.created_by_name.trim(), // Keep original case for display
            nameKey: userName, // Use lowercase for grouping
            iprCount: 0,
            approvedCount: 0,
            rejectedCount: 0,
            pendingCount: 0,
          };
        }

        userRanking[userName].iprCount += 1;

        // Count by status
        const status = ipr.status || "pending";
        if (status === "approved" || status === "complete") {
          userRanking[userName].approvedCount += 1;
        } else if (
          status === "rejected" ||
          status === "error" ||
          status === "cancelled"
        ) {
          userRanking[userName].rejectedCount += 1;
        } else {
          userRanking[userName].pendingCount += 1;
        }
      }
    });

    // Convert to array and sort by IPR count, then map to UserRankingChart format
    const userRankingArray = Object.values(userRanking)
      .sort((a, b) => b.iprCount - a.iprCount)
      .slice(0, 5) // Top 5 users
      .map((user) => ({
        name: user.name,
        requests: user.iprCount,
        amount: user.iprCount, // Use IPR count as amount for the chart
        // Keep original data for potential future use
        originalData: user,
      }));

    // Procesar tiempo de aprobación usando IPRs aprobados
    const approvalTimeData = [];

    // Debug: Log first few IPRs to see their structure
    if (iprData.length > 0) {
      console.log("Sample IPR structure:", {
        firstIPR: iprData[0],
        fields: Object.keys(iprData[0]),
        status: iprData[0].status,
        created_at: iprData[0].created_at,
        updated_at: iprData[0].updated_at,
        extraction_date: iprData[0].extraction_date,
      });
    }

    iprData.forEach((ipr) => {
      // Solo procesar IPRs que tengan estado aprobado y fechas válidas
      if (
        (ipr.status === "approved" || ipr.status === "complete") &&
        ipr.created_at &&
        ipr.updated_at
      ) {
        try {
          const creationDate = new Date(ipr.created_at);
          const approvalDate = new Date(ipr.updated_at);

          // Calcular diferencia en días (puede ser menos de 1 día)
          const timeDiff = approvalDate.getTime() - creationDate.getTime();
          const daysDiff = timeDiff / (1000 * 3600 * 24); // Mantener decimales para sub-días

          if (daysDiff >= 0 && daysDiff <= 365) {
            // Filtrar valores razonables (0-365 días)
            approvalTimeData.push({
              iprId: ipr.id,
              days: daysDiff,
              amount: ipr.total_amount
                ? parseFloat(ipr.total_amount.replace(/[^\d.-]/g, "")) || 0
                : 0,
              area: ipr.area || "Sin área",
              createdBy: ipr.created_by_name || "Sin usuario",
              approvalDate: approvalDate,
              creationDate: creationDate,
            });
          }
        } catch (error) {
          console.error(
            "Error processing approval time for IPR:",
            ipr.id,
            error
          );
          // Ignorar fechas inválidas
        }
      }
    });

    // Agrupar por rangos de tiempo para el gráfico
    const approvalTimeRanges = {
      "< 1 día": 0,
      "1-2 días": 0,
      "3-7 días": 0,
      "8-14 días": 0,
      "15-30 días": 0,
      "> 30 días": 0,
    };

    approvalTimeData.forEach((item) => {
      if (item.days < 1) {
        approvalTimeRanges["< 1 día"]++;
      } else if (item.days < 2) {
        approvalTimeRanges["1-2 días"]++;
      } else if (item.days < 7) {
        approvalTimeRanges["3-7 días"]++;
      } else if (item.days < 14) {
        approvalTimeRanges["8-14 días"]++;
      } else if (item.days < 30) {
        approvalTimeRanges["15-30 días"]++;
      } else {
        approvalTimeRanges["Más de 30 días"]++;
      }
    });

    // Convertir a formato de gráfico
    const approvalTimeChartData = Object.entries(approvalTimeRanges).map(
      ([range, count]) => ({
        range,
        count,
        percentage:
          approvalTimeData.length > 0
            ? Math.round((count / approvalTimeData.length) * 100)
            : 0,
      })
    );

    // Debug: Log approval time data
    console.log("Approval Time Data:", {
      totalIPRs: iprData.length,
      approvedIPRs: iprData.filter(
        (ipr) => ipr.status === "approved" || ipr.status === "complete"
      ).length,
      approvalTimeData: approvalTimeData,
      approvalTimeRanges: approvalTimeRanges,
    });

    // Debug: Log the final chart data
    console.log("Final Approval Time Chart Data:", approvalTimeChartData);
    console.log("Final Approval Time Stats:", {
      totalApproved: approvalTimeData.length,
      averageDays:
        approvalTimeData.length > 0
          ? Math.round(
              approvalTimeData.reduce((sum, item) => sum + item.days, 0) /
                approvalTimeData.length
            )
          : 0,
    });

    // Debug: Check if chart data has any non-zero counts
    const hasData = approvalTimeChartData.some((item) => item.count > 0);
    console.log(
      "Chart has data:",
      hasData,
      "Chart data structure:",
      approvalTimeChartData
    );

    return {
      globalStatus: {
        approved: statusCounts.approved || 0,
        rejected: statusCounts.rejected || 0,
        inProcess: statusCounts.inProcess || 0,
      },
      amountByStatus: {
        approved: amountByStatus.approved || 0,
        rejected: amountByStatus.rejected || 0,
        inProcess: amountByStatus.inProcess || 0,
      },
      monthlyEvolution: monthlyEvolution,
      userRanking: userRankingArray,
      approvalTime: approvalTimeChartData,
      approvalTimeStats: {
        totalApproved: approvalTimeData.length,
        averageDays:
          approvalTimeData.length > 0
            ? Math.round(
                approvalTimeData.reduce((sum, item) => sum + item.days, 0) /
                  approvalTimeData.length
              )
            : 0,
        fastestApproval:
          approvalTimeData.length > 0
            ? Math.min(...approvalTimeData.map((item) => item.days))
            : 0,
        slowestApproval:
          approvalTimeData.length > 0
            ? Math.max(...approvalTimeData.map((item) => item.days))
            : 0,
      },
    };
  }, [iprData]);

  // Función para formatear monedas (desde IPRDashboard)
  const formatCurrency = (amount, currency = "USD") => {
    if (!amount || isNaN(amount)) return "0";

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return `${currency} ${amount.toLocaleString()}`;
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Aquí iría la llamada a la API para obtener datos del dashboard
      // const response = await getRequisitionsDashboardData(mantisBusiness._id, token, selectedTimeRange, selectedFilters);

      // Datos mock para desarrollo
      const mockData = {
        globalStatus: {
          approved: 150,
          rejected: 25,
          inProcess: 45,
        },
        amountByStatus: {
          approved: 2500000,
          rejected: 350000,
          inProcess: 750000,
        },
        monthlyEvolution: [
          { month: "Ene", approved: 180000, rejected: 45000 },
          { month: "Feb", approved: 220000, rejected: 38000 },
          { month: "Mar", approved: 195000, rejected: 52000 },
          { month: "Abr", approved: 240000, rejected: 41000 },
        ],
        paymentProjection: [
          { month: "May", amount: 185000 },
          { month: "Jun", amount: 220000 },
          { month: "Jul", amount: 198000 },
        ],
        executionVsBudget: {
          executed: 1850000,
          projected: 2200000,
          budget: 2500000,
        },
      };

      setDashboardData(mockData);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Nueva función para cargar datos de IPR
  const loadIPRData = async () => {
    try {
      setIprLoading(true);
      setIprError(null);

      if (mantisBusiness?._id) {
        const response = await getWorkflowResultsp2p(mantisBusiness._id);
        setIprData(response.data || []);
      } else {
        // No hay mantisBusiness, no cargar datos mock
        setIprData([]);
      }
    } catch (error) {
      console.error("Error loading IPR data:", error);
      setIprError(error.message || "Error al cargar datos de IPR");
      // En caso de error, no usar datos mock
      setIprData([]);
    } finally {
      setIprLoading(false);
    }
  };

  // Procesar datos reales de los IPRs (desde IPRDashboard)
  const processedIPRData = useMemo(() => {
    if (!iprData || iprData.length === 0) return {};

    // Estadísticas por estado
    const statusStats = iprData.reduce((acc, ipr) => {
      const status = ipr.status || "pending";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Estadísticas por canal de origen
    const channelStats = iprData.reduce((acc, ipr) => {
      let channel = ipr.origin_channel || "unknown";

      // Normalizar nombres de canales para evitar duplicados
      if (channel) {
        channel = channel.toLowerCase().trim();

        // Mapear variaciones a nombres estándar
        if (channel === "email" || channel === "e-mail" || channel === "mail") {
          channel = "email";
        } else if (
          channel === "whatsapp" ||
          channel === "wa" ||
          channel === "whats"
        ) {
          channel = "whatsapp";
        } else if (
          channel === "form" ||
          channel === "formulario" ||
          channel === "webform"
        ) {
          channel = "form";
        } else if (
          channel === "unknown" ||
          channel === "sin especificar" ||
          channel === "" ||
          channel === "n/a"
        ) {
          channel = "unknown";
        }
      }

      acc[channel] = (acc[channel] || 0) + 1;
      return acc;
    }, {});

    // Estadísticas por mes (últimos 6 meses)
    const monthlyStats = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString("es-ES", { month: "short" });
      monthlyStats[monthKey] = 0;
    }

    iprData.forEach((ipr) => {
      if (ipr.extraction_date) {
        try {
          const iprDate = new Date(ipr.extraction_date);
          const monthKey = iprDate.toLocaleDateString("es-ES", {
            month: "short",
          });
          if (monthlyStats.hasOwnProperty(monthKey)) {
            monthlyStats[monthKey]++;
          }
        } catch (error) {
          // Ignorar fechas inválidas
        }
      }
    });

    // Estadísticas por área (si está disponible)
    const areaStats = iprData.reduce((acc, ipr) => {
      const area = ipr.area || "Sin área";
      acc[area] = (acc[area] || 0) + 1;
      return acc;
    }, {});

    // Calcular montos totales por estado
    const amountByStatus = iprData.reduce((acc, ipr) => {
      const status = ipr.status || "pending";
      let amount = 0;
      if (ipr.total_amount && typeof ipr.total_amount === "string") {
        amount = parseFloat(ipr.total_amount.replace(/[^\d.-]/g, "")) || 0;
      }
      acc[status] = (acc[status] || 0) + amount;
      return acc;
    }, {});

    // Procesar datos de cost center allocations
    const costCenterStats = {};
    iprData.forEach((ipr) => {
      if (
        ipr.cost_center_allocations &&
        Array.isArray(ipr.cost_center_allocations)
      ) {
        ipr.cost_center_allocations.forEach((allocation) => {
          if (allocation.cost_center && allocation.amount) {
            const costCenterId = allocation.cost_center.id;
            const costCenterName =
              allocation.cost_center.name || `CC-${costCenterId.slice(-4)}`;
            const amount = parseFloat(allocation.amount) || 0;
            const currency = allocation.currency || "USD";

            if (!costCenterStats[costCenterId]) {
              costCenterStats[costCenterId] = {
                id: costCenterId,
                name: costCenterName,
                totalAmount: 0,
                currency: currency,
                allocationCount: 0,
                iprCount: 0,
              };
            }

            costCenterStats[costCenterId].totalAmount += amount;
            costCenterStats[costCenterId].allocationCount += 1;

            // Contar IPRs únicos por cost center
            if (!costCenterStats[costCenterId].iprIds) {
              costCenterStats[costCenterId].iprIds = new Set();
            }
            costCenterStats[costCenterId].iprIds.add(ipr.id);
          }
        });
      }
    });

    // Calcular IPR count para cada cost center
    Object.values(costCenterStats).forEach((cc) => {
      cc.iprCount = cc.iprIds ? cc.iprIds.size : 0;
      delete cc.iprIds; // Limpiar el Set temporal
    });

    return {
      statusStats,
      channelStats,
      monthlyStats,
      areaStats,
      amountByStatus,
      costCenterStats,
      total: iprData.length,
    };
  }, [iprData]);

  // Procesar datos reales de cost centers para el ranking
  const realRankingCecos = useMemo(() => {
    if (!processedIPRData.costCenterStats) return [];

    // Convertir a array y ordenar por monto total
    const costCenterArray = Object.values(processedIPRData.costCenterStats)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 10); // Top 10 cost centers

    // Calcular porcentaje de ejecución (simulado por ahora - se puede ajustar con datos de presupuesto real)
    return costCenterArray.map((cc, index) => {
      // Simular porcentaje de ejecución basado en el ranking y monto
      // En un escenario real, esto vendría de datos de presupuesto vs ejecutado
      const baseExecution = 100 - index * 8; // De 100% a 20%
      const executionPercentage = Math.max(20, baseExecution);

      return {
        ceco: cc.name,
        ejecucion: executionPercentage,
        amount: cc.totalAmount,
        currency: cc.currency,
        iprCount: cc.iprCount,
        allocationCount: cc.allocationCount,
      };
    });
  }, [processedIPRData.costCenterStats]);

  // Datos estáticos para gráficos de negocio general
  const dataEjecucion = useMemo(
    () =>
      sampleMonths.map((m, i) => ({
        mes: m,
        ejecutado: 40 + i * 3,
        aprobado: 50 + i * 2,
        asignado: 80,
      })),
    []
  );

  const estadoSolicitudes = [
    { estado: "Aprobada", cantidad: 120 },
    { estado: "En Revisión", cantidad: 80 },
    { estado: "Pendiente", cantidad: 45 },
    { estado: "Rechazada", cantidad: 20 },
  ];

  // Usar solo datos reales, no mostrar datos fake
  const rankingCecos = realRankingCecos;

  const pieCats = [
    { name: "Capex", value: 420 },
    { name: "Opex", value: 680 },
    { name: "Servicios", value: 260 },
  ];

  const scatterProv = [
    { x: 3, y: 120 },
    { x: 8, y: 520 },
    { x: 12, y: 900 },
    { x: 5, y: 300 },
    { x: 15, y: 1400 },
  ];

  // Preparar datos para los gráficos de IPR
  const iprStatusChartData = useMemo(() => {
    if (!processedIPRData.statusStats) return [];

    const statusLabels = {
      pending: "Pendiente",
      complete: "Completado",
      approved: "Aprobado",
      error: "Rechazado",
    };

    return Object.entries(processedIPRData.statusStats).map(
      ([status, count]) => ({
        estado: statusLabels[status] || status,
        cantidad: count,
        status: status,
      })
    );
  }, [processedIPRData.statusStats]);

  const iprChannelChartData = useMemo(() => {
    if (!processedIPRData.channelStats) return [];

    const channelLabels = {
      form: "Formulario",
      whatsapp: "WhatsApp",
      email: "Email",
      unknown: "Sin especificar",
    };

    // Ordenar por cantidad descendente para mejor visualización
    return Object.entries(processedIPRData.channelStats)
      .sort(([, a], [, b]) => b - a) // Ordenar por cantidad descendente
      .map(([channel, count]) => ({
        name: channelLabels[channel] || channel,
        value: count,
        originalChannel: channel, // Mantener referencia al canal original para debugging
      }));
  }, [processedIPRData.channelStats]);

  const iprMonthlyChartData = useMemo(() => {
    if (!processedIPRData.monthlyStats) return [];

    return Object.entries(processedIPRData.monthlyStats).map(
      ([month, count]) => ({
        mes: month,
        cantidad: count,
      })
    );
  }, [processedIPRData.monthlyStats]);

  const iprAreaChartData = useMemo(() => {
    if (!processedIPRData.areaStats) return [];

    // Crear un objeto para acumular tanto cantidad como montos por área
    const areaData = {};

    // Procesar IPRs para obtener estadísticas por área
    iprData.forEach((ipr) => {
      const area = ipr.area || "Sin área";
      if (!areaData[area]) {
        areaData[area] = {
          count: 0,
          amount: 0,
          currency: "USD",
        };
      }

      areaData[area].count += 1;

      // Procesar monto si está disponible
      if (ipr.total_amount && typeof ipr.total_amount === "string") {
        const amount =
          parseFloat(ipr.total_amount.replace(/[^\d.-]/g, "")) || 0;
        areaData[area].amount += amount;
      }
    });

    // Convertir a array y ordenar por cantidad, luego tomar top 5
    return Object.entries(areaData)
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5)
      .map(([area, data]) => ({
        area: area.length > 20 ? area.substring(0, 20) + "..." : area,
        cantidad: data.count,
        monto: data.amount,
        currency: data.currency,
      }));
  }, [iprData, processedIPRData.areaStats]);

  const iprAmountChartData = useMemo(() => {
    if (!processedIPRData.amountByStatus) return [];

    const statusLabels = {
      pending: "Pendiente",
      complete: "Completado",
      approved: "Aprobado",
      error: "Rechazado",
    };

    return Object.entries(processedIPRData.amountByStatus).map(
      ([status, amount]) => ({
        estado: statusLabels[status] || status,
        monto: Math.round(amount / 1000), // Convertir a miles
        status: status,
      })
    );
  }, [processedIPRData.amountByStatus]);

  // Colores para los gráficos
  const chartColors = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444"];

  const handleTimeRangeChange = (timeRange) => {
    setSelectedTimeRange(timeRange);
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  if (loading) {
    return (
      <div className="requisitions-dashboard-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="requisitions-dashboard"
    >
      {/* Dashboard Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-blue-500 rounded">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Dashboard de Requisiciones
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Métricas de negocio y requisiciones
                {iprLoading && (
                  <span className="ml-2 text-blue-500">(Cargando IPRs...)</span>
                )}
                {iprError && (
                  <span className="ml-2 text-red-500">(Error: {iprError})</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="p-6">
        <div className="min-h-screen bg-background text-foreground">
          <header className="relative overflow-hidden">
            <div className="container py-10 md:py-14">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                {/* <div>
                  <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                    Control Presupuestal Automatizado
                  </h1>
                  <p className="text-muted-foreground mt-2 max-w-2xl">
                    Dashboard para monitorear ejecución presupuestal, estado de
                    solicitudes y cumplimiento por CECO, área y empresa.
                  </p>
                </div> */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={loadIPRData}
                    disabled={iprLoading}
                    className="flex items-center gap-2"
                  >
                    {iprLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        Actualizando
                      </>
                    ) : (
                      "🔄 Actualizar IPRs"
                    )}
                  </Button>
                  {/* <Button variant="default">Exportar</Button>
                  <Button variant="outline">Compartir</Button> */}
                </div>
              </div>
            </div>
          </header>

          <main className="container pb-16 space-y-12">
            {/* Sección: Dashboard de Requisiciones Original */}
            <section>
              {/* 
              <div className="dashboard-filters mb-6">
                <div className="filters-row">
                  <select
                    value={selectedTimeRange}
                    onChange={(e) => handleTimeRangeChange(e.target.value)}
                    className="time-range-selector"
                  >
                    <option value="30d">Últimos 30 días</option>
                    <option value="3m">Últimos 3 meses</option>
                    <option value="6m">Últimos 6 meses</option>
                    <option value="12m">Últimos 12 meses</option>
                  </select>

                  <select
                    value={selectedFilters.company || ""}
                    onChange={(e) =>
                      handleFilterChange("company", e.target.value)
                    }
                    className="filter-select"
                  >
                    <option value="">Todas las empresas</option>
                   
                  </select>

                  <select
                    value={selectedFilters.division || ""}
                    onChange={(e) =>
                      handleFilterChange("division", e.target.value)
                    }
                    className="filter-select"
                  >
                    <option value="">Todas las divisiones</option>
                 
                  </select>

                  <select
                    value={selectedFilters.costCenter || ""}
                    onChange={(e) =>
                      handleFilterChange("costCenter", e.target.value)
                    }
                    className="filter-select"
                  >
                    <option value="">Todos los centros de costo</option>
                    
                  </select>

                  <select
                    value={selectedFilters.status || ""}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    className="filter-select"
                  >
                    <option value="">Todos los estados</option>
                    <option value="approved">Aprobadas</option>
                    <option value="rejected">Rechazadas</option>
                    <option value="inProcess">En proceso</option>
                  </select>
                </div>
              </div> */}

              <div className="dashboard-grid">
                {/* Fila 1: Métricas principales */}
                <div className="dashboard-row">
                  <div className="chart-container large">
                    {iprLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-sm text-muted-foreground">
                            Cargando datos de IPR...
                          </p>
                        </div>
                      </div>
                    ) : processIPRForDashboard?.globalStatus ? (
                      <GlobalStatusChart
                        data={processIPRForDashboard.globalStatus}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">
                            No hay datos de IPR disponibles
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* <div className="chart-container large">
                    {iprLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-sm text-muted-foreground">
                            Cargando datos de IPR...
                          </p>
                        </div>
                      </div>
                    ) : processIPRForDashboard?.amountByStatus ? (
                      <AmountByStatusChart
                        data={processIPRForDashboard.amountByStatus}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">
                            No hay datos de IPR disponibles
                          </p>
                        </div>
                      </div>
                    )}
                  </div> */}
                </div>

                {/* Fila 2: Evolución temporal */}
                {/* <div className="dashboard-row">
                  <div className="chart-container xl">
                    {iprLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-sm text-muted-foreground">
                            Cargando datos de IPR...
                          </p>
                        </div>
                      </div>
                    ) : processIPRForDashboard?.monthlyEvolution ? (
                      <MonthlyEvolutionChart
                        data={processIPRForDashboard.monthlyEvolution}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">
                            No hay datos de IPR disponibles
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="chart-container large">
                    <PaymentProjectionChart
                      data={dashboardData.paymentProjection}
                    />
                  </div>
                </div> */}

                {/* Fila 3: Presupuesto y usuarios */}
                <div className="dashboard-row">
                  {/* <div className="chart-container large">
                    <ExecutionVsBudgetChart
                      data={dashboardData.executionVsBudget}
                    />
                  </div> */}
                  <div className="chart-container large">
                    {iprLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-sm text-muted-foreground">
                            Cargando datos de IPR...
                          </p>
                        </div>
                      </div>
                    ) : processIPRForDashboard?.userRanking ? (
                      <UserRankingChart
                        data={processIPRForDashboard.userRanking}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">
                            No hay datos de usuarios disponibles
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Debug: processIPRForDashboard ={" "}
                            {JSON.stringify(processIPRForDashboard)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Fila 4: Centros de costo y empresas */}
                {/* <div className="dashboard-row">
                  <div className="chart-container large">
                    <CostCenterRankingChart
                      data={dashboardData.costCenterRanking}
                    />
                  </div>
                  <div className="chart-container large">
                    <RequisitionsByCompanyChart
                      data={dashboardData.requisitionsByCompany}
                    />
                  </div>
                </div> */}

                {/* Fila 5: Distribución y tiempos */}
                <div className="dashboard-row">
                  {/* <div className="chart-container medium">
                    <AmountDistributionChart
                      data={dashboardData.amountDistribution}
                    />
                  </div> */}
                  <div className="chart-container medium">
                    {iprLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-sm text-muted-foreground">
                            Cargando datos de IPR...
                          </p>
                        </div>
                      </div>
                    ) : processIPRForDashboard?.approvalTime ? (
                      <div className="space-y-4">
                        {/* Estadísticas de tiempo de aprobación */}
                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {
                                processIPRForDashboard.approvalTimeStats
                                  .averageDays
                              }
                            </p>
                            <p className="text-xs text-blue-600 dark:text-blue-400">
                              Días promedio
                            </p>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                              {
                                processIPRForDashboard.approvalTimeStats
                                  .totalApproved
                              }
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400">
                              IPRs aprobados
                            </p>
                          </div>
                        </div>
                        {/* Gráfico de tiempo de aprobación */}
                        <ApprovalTimeChart
                          data={processIPRForDashboard.approvalTime}
                          stats={processIPRForDashboard.approvalTimeStats}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <p className="text-sm text-muted-foreground">
                            No hay datos de tiempo de aprobación disponibles
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* <div className="chart-container medium">
                    <WeeklyVolumeChart data={dashboardData.weeklyVolume} />
                  </div> */}
                </div>

                {/* Fila 6: Análisis avanzado */}
                {/* <div className="dashboard-row">
                  <div className="chart-container large">
                    <RejectionRateChart data={dashboardData.rejectionRate} />
                  </div>
                  <div className="chart-container large">
                    <CategoryExpenseChart
                      data={dashboardData.categoryExpense}
                    />
                  </div>
                </div> */}

                {/* Fila 7: Uso y jerarquía */}
                {/* <div className="dashboard-row">
                  <div className="chart-container large">
                    <UserUsageChart data={dashboardData.userUsage} />
                  </div>
                  <div className="chart-container xl">
                    <HierarchicalMapChart
                      data={dashboardData.hierarchicalMap}
                    />
                  </div>
                </div> */}

                {/* Fila 8: IPR Dashboard - Ejecución Presupuestal */}
                {/* <div className="dashboard-row">
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Ejecución Presupuestal</CardTitle>
                        <CardDescription>Avance por mes</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={dataEjecucion}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="mes" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar
                                  dataKey="ejecutado"
                                  name="Ejecutado"
                                  fill="#3b82f6"
                                />
                                <Bar
                                  dataKey="aprobado"
                                  name="Aprobado"
                                  fill="#8b5cf6"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Evolución Mensual</CardTitle>
                        <CardDescription>
                          Ejecutado vs Aprobado vs Asignado
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={dataEjecucion}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="mes" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line
                                  type="monotone"
                                  dataKey="ejecutado"
                                  name="Ejecutado"
                                  stroke="#3b82f6"
                                  strokeWidth={2}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="aprobado"
                                  name="Aprobado"
                                  stroke="#8b5cf6"
                                  strokeWidth={2}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="asignado"
                                  name="Asignado"
                                  stroke="#6b7280"
                                  strokeDasharray="4 4"
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div> */}

                {/* Fila 9: IPR Dashboard - Estado de Solicitudes */}
                <div className="dashboard-row">
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Estado de IPRs</CardTitle>
                        <CardDescription>
                          Cantidad por estado de validación
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprStatusChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={iprStatusChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="estado" />
                                <YAxis />
                                <Tooltip />
                                <Bar
                                  dataKey="cantidad"
                                  name="Cantidad"
                                  fill="#3b82f6"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Distribución por Categoría</CardTitle>
                        <CardDescription>Proporción del gasto</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieCats}
                                  dataKey="value"
                                  nameKey="name"
                                  innerRadius={60}
                                  outerRadius={90}
                                  paddingAngle={4}
                                >
                                  {pieCats.map((_, i) => (
                                    <Cell
                                      key={i}
                                      fill={chartColors[i % chartColors.length]}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div> */}
                </div>

                {/* Fila 10: IPR Dashboard - Desempeño por CECO */}
                <div className="dashboard-row">
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Ranking de Centros de Costos</CardTitle>
                        <CardDescription>
                          {realRankingCecos.length > 0
                            ? `Top ${realRankingCecos.length} Centros de Costos por monto total`
                            : "Esperando datos de IPR"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : rankingCecos.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={rankingCecos}
                                layout="vertical"
                                margin={{ left: 24 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 100]} hide />
                                <YAxis
                                  dataKey="ceco"
                                  type="category"
                                  width={80}
                                />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                                          <p className="font-semibold">
                                            {data.ceco}
                                          </p>
                                          <p className="text-sm text-slate-600 dark:text-slate-400">
                                            Ejecución: {data.ejecucion}%
                                          </p>
                                          {data.amount && (
                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                              Monto:{" "}
                                              {formatCurrency(
                                                data.amount,
                                                data.currency
                                              )}
                                            </p>
                                          )}
                                          {data.iprCount && (
                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                              IPRs: {data.iprCount}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar
                                  dataKey="ejecucion"
                                  name="Ejecución %"
                                  fill="#10b981"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de Centros de Costos disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Montos por Centro de Costos</CardTitle>
                        <CardDescription>
                          {realRankingCecos.length > 0
                            ? "Distribución real de montos por Centro de Costos"
                            : "Esperando datos de IPR"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : rankingCecos.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={rankingCecos}
                                layout="vertical"
                                margin={{ left: 24 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis
                                  dataKey="ceco"
                                  type="category"
                                  width={80}
                                />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                                          <p className="font-semibold">
                                            {data.ceco}
                                          </p>
                                          {data.amount && (
                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                              Monto:{" "}
                                              {formatCurrency(
                                                data.amount,
                                                data.currency
                                              )}
                                            </p>
                                          )}
                                          {data.iprCount && (
                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                              IPRs: {data.iprCount}
                                            </p>
                                          )}
                                          {data.allocationCount && (
                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                              Asignaciones:{" "}
                                              {data.allocationCount}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar
                                  dataKey="amount"
                                  name="Monto"
                                  fill="#f59e0b"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de CECOs disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Fila 11: IPR Dashboard - Canales y Áreas */}
                <div className="dashboard-row">
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>IPRs por Canal</CardTitle>
                        <CardDescription>
                          Cantidad por canal de origen
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprChannelChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={iprChannelChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Bar
                                  dataKey="value"
                                  name="Cantidad"
                                  fill="#8b5cf6"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="chart-container large">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                              Top 5 Áreas
                            </CardTitle>
                            <CardDescription className="text-slate-600 dark:text-slate-400">
                              Áreas con más IPRs
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="h-72">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprAreaChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={iprAreaChartData}
                                layout="vertical"
                                margin={{ left: 24 }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="#e2e8f0"
                                />
                                <XAxis
                                  type="number"
                                  tick={{ fontSize: 12, fill: "#64748b" }}
                                  axisLine={{ stroke: "#e2e8f0" }}
                                />
                                <YAxis
                                  dataKey="area"
                                  type="category"
                                  width={100}
                                  tick={{ fontSize: 12, fill: "#64748b" }}
                                  axisLine={{ stroke: "#e2e8f0" }}
                                />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl">
                                          <div className="flex items-center gap-2 mb-2">
                                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                            <p className="font-semibold text-slate-900 dark:text-slate-100">
                                              {data.area}
                                            </p>
                                          </div>
                                          <div className="space-y-1">
                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                              <span className="font-medium">
                                                Cantidad:
                                              </span>{" "}
                                              {data.cantidad} IPRs
                                            </p>
                                            {data.monto && (
                                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                                <span className="font-medium">
                                                  Monto:
                                                </span>{" "}
                                                {formatCurrency(
                                                  data.monto,
                                                  data.currency
                                                )}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar
                                  dataKey="cantidad"
                                  name="Cantidad"
                                  fill="url(#redGradient)"
                                  radius={[0, 4, 4, 0]}
                                />
                                <defs>
                                  <linearGradient
                                    id="redGradient"
                                    x1="0"
                                    y1="0"
                                    x2="1"
                                    y2="0"
                                  >
                                    <stop
                                      offset="0%"
                                      stopColor="#ef4444"
                                      stopOpacity={0.8}
                                    />
                                    <stop
                                      offset="100%"
                                      stopColor="#dc2626"
                                      stopOpacity={1}
                                    />
                                  </linearGradient>
                                </defs>
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Fila 11.5: IPR Dashboard - Distribución por Área */}
                <div className="dashboard-row">
                  <div className="chart-container large">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <PieChartIconLucide className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                              Distribución por Área
                            </CardTitle>
                            <CardDescription className="text-slate-600 dark:text-slate-400">
                              Gráfico de distribución organizacional
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="h-96">
                          <AreaDistributionChart
                            data={iprAreaChartData}
                            loading={iprLoading}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* <div className="chart-container large">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                              Monto total por división
                            </CardTitle>
                            <CardDescription className="text-slate-600 dark:text-slate-400">
                              Distribución de montos por área
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="h-72">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprAreaChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={iprAreaChartData}>
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="#e2e8f0"
                                />
                                <XAxis
                                  dataKey="area"
                                  tick={{ fontSize: 12, fill: "#64748b" }}
                                  axisLine={{ stroke: "#e2e8f0" }}
                                />
                                <YAxis
                                  tick={{ fontSize: 12, fill: "#64748b" }}
                                  axisLine={{ stroke: "#e2e8f0" }}
                                />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl">
                                          <div className="flex items-center gap-2 mb-2">
                                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                            <p className="font-semibold text-slate-900 dark:text-slate-100">
                                              {data.area}
                                            </p>
                                          </div>
                                          <div className="space-y-1">
                                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                              <span className="font-medium">
                                                Cantidad:
                                              </span>{" "}
                                              {data.cantidad} IPRs
                                            </p>
                                            {data.monto && (
                                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                                <span className="font-medium">
                                                  Monto:
                                                </span>{" "}
                                                {formatCurrency(
                                                  data.monto,
                                                  data.currency
                                                )}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar
                                  dataKey="monto"
                                  name="Monto"
                                  fill="url(#greenGradient)"
                                  radius={[4, 4, 0, 0]}
                                />
                                <defs>
                                  <linearGradient
                                    id="greenGradient"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                  >
                                    <stop
                                      offset="0%"
                                      stopColor="#10b981"
                                      stopOpacity={0.8}
                                    />
                                    <stop
                                      offset="100%"
                                      stopColor="#059669"
                                      stopOpacity={1}
                                    />
                                  </linearGradient>
                                </defs>
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div> */}
                </div>

                {/* Fila 12: IPR Dashboard - Cumplimiento y Riesgos */}
                {/* <div className="dashboard-row">
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Solicitudes fuera de Política</CardTitle>
                        <CardDescription>Indicador general</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={dataEjecucion}>
                                <defs>
                                  <linearGradient
                                    id="grad"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                  >
                                    <stop
                                      offset="5%"
                                      stopColor="#3b82f6"
                                      stopOpacity={0.8}
                                    />
                                    <stop
                                      offset="95%"
                                      stopColor="#3b82f6"
                                      stopOpacity={0.1}
                                    />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="mes" />
                                <YAxis />
                                <Tooltip />
                                <Area
                                  type="monotone"
                                  dataKey="ejecutado"
                                  stroke="#3b82f6"
                                  fillOpacity={1}
                                  fill="url(#grad)"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="chart-container large">
                    <Card>
                      <CardHeader>
                        <CardTitle>Riesgos por Tipo</CardTitle>
                        <CardDescription>Distribución</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {iprLoading ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-sm text-muted-foreground">
                                  Cargando datos de IPR...
                                </p>
                              </div>
                            </div>
                          ) : iprData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieCats}
                                  dataKey="value"
                                  nameKey="name"
                                  outerRadius={90}
                                >
                                  {pieCats.map((_, i) => (
                                    <Cell
                                      key={i}
                                      fill={chartColors[i % chartColors.length]}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <p className="text-sm text-muted-foreground">
                                  No hay datos de IPR disponibles
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div> */}
              </div>
            </section>
          </main>
        </div>
      </div>
    </motion.div>
  );
};

const mapStateToProps = (state) => ({
  ...selectAuthState(state),
});

export default connect(mapStateToProps)(RequisitionsDashboard);
